import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const LiveVotingForCader = () => {
    const navigate = useNavigate();
    const { language, appId, userId } = useAppId();
    const lang = language || 'en';

    // --- State ---
    const [booths, setBooths] = useState([]);

    // --- Popup Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedBooth, setSelectedBooth] = useState(null);
    const [voterNumber, setVoterNumber] = useState("");
    const [voterNumbersList, setVoterNumbersList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Live Voting",
            booth: "Booth",
            done: "Done",
            remain: "Remain",
            addVoting: "Add Voting",
            noBooth: "No booth found.",
            modalTitle: "Add Voter Numbers",
            enterNumber: "Enter Voter Number",
            addNumber: "Add Number",
            addedNumbers: "Added Numbers",
            submit: "Submit",
            cancel: "Cancel",
            noNumbers: "No numbers added yet",
            successMessage: "Voting data submitted successfully!",
            errorMessage: "Failed to submit voting data."
        },
        hi: {
            pageTitle: "लाइव वोटिंग",
            booth: "Booth",
            done: "Done",
            remain: "Remain",
            addVoting: "Add Voting",
            noBooth: "कोई बूथ नहीं मिला।",
            modalTitle: "मतदाता संख्या जोड़ें",
            enterNumber: "मतदाता संख्या दर्ज करें",
            addNumber: "संख्या जोड़ें",
            addedNumbers: "जोड़ी गई संख्याएं",
            submit: "जमा करें",
            cancel: "रद्द करें",
            noNumbers: "अभी तक कोई संख्या नहीं जोड़ी गई",
            successMessage: "वोटिंग डेटा सफलतापूर्वक जमा कर दिया गया!",
            errorMessage: "वोटिंग डेटा जमा करने में विफल।"
        },
        gu: {
            pageTitle: "લાઈવ વોટિંગ",
            booth: "Booth",
            done: "Done",
            remain: "Remain",
            addVoting: "Add Voting",
            noBooth: "કોઈ બૂથ મળ્યું નથી.",
            modalTitle: "મતદાર નંબર ઉમેરો",
            enterNumber: "મતદાર નંબર દાખલ કરો",
            addNumber: "નંબર ઉમેરો",
            addedNumbers: "ઉમેરેલા નંબરો",
            submit: "સબમિટ કરો",
            cancel: "રદ કરો",
            noNumbers: "હજુ સુધી કોઈ નંબર ઉમેરાયો નથી",
            successMessage: "વોટિંગ ડેટા સફળતાપૂર્વક સબમિટ થયો!",
            errorMessage: "વોટિંગ ડેટા સબમિટ કરવામાં નિષ્ફળ."
        }
    };

    const txt = t[lang] || t.gu;

    useEffect(() => {
        // Get booth numbers from localStorage
        const storedBooths = localStorage.getItem('boothNo');

        if (storedBooths) {
            const boothArray = storedBooths.split(',').map(b => b.trim()).filter(b => b);
            setBooths(boothArray);
        } else {
            setBooths([]);
        }
    }, []);

    const handleCardClick = (booth) => {
        // Navigate to dis_booth_wise_live_voting_voter with booth number
        navigate('/dis_booth_wise_live_voting_voter', { state: { boothNo: booth } });
    };

    const handleAddVoting = (boothNo, e) => {
        e.stopPropagation(); // Prevent card click from triggering
        setSelectedBooth(boothNo);
        setVoterNumber("");
        setVoterNumbersList([]);
        setIsModalOpen(true);
    };

    const handleAddNumber = () => {
        if (voterNumber && voterNumber.trim()) {
            setVoterNumbersList([...voterNumbersList, voterNumber.trim()]);
            setVoterNumber("");
        }
    };

    const handleRemoveNumber = (index) => {
        const updatedList = voterNumbersList.filter((_, i) => i !== index);
        setVoterNumbersList(updatedList);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddNumber();
        }
    };

    const handleSubmit = async () => {
        if (voterNumbersList.length === 0) {
            alert("Please add at least one voter number");
            return;
        }

        // Prepare JSON data for API
        const jsonRowsArray = voterNumbersList.map(number => ({
            app_id: String(appId),
            slnoinpart: number,
            part_no: String(selectedBooth)
        }));

        // Stringify the array - this is what the API expects
        const jsonRowsString = JSON.stringify(jsonRowsArray);

        console.log("Submitting JSON data:", jsonRowsString);

        setIsSubmitting(true);

        try {
            const response = await axios.post(`${BASE_URL}/api/ins_live_voting_bulk`, {
                app_id:appId,
                json_rows: jsonRowsString,
                created_by: String(userId)
            });

            console.log("API Response:", jsonRowsString);

            if (response.data.success) {
                alert(txt.successMessage);
                setIsModalOpen(false);
                setVoterNumbersList([]);
                setVoterNumber("");
            } else {
                alert(txt.errorMessage + ": " + (response.data.message || response.data.error || "Unknown error"));
            }
        } catch (error) {
            console.error("Error submitting voting data:", error);
            alert(txt.errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="lv-page-container">
            {/* Header */}
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">

                {booths.length > 0 ? (
                    <div className="row g-3 slide-up-element">
                        {booths.map((booth, index) => (
                            <div className="col-12 col-sm-6 col-md-4" key={index}>
                                <div className="lv-card" onClick={() => handleCardClick(booth)} style={{ cursor: 'pointer' }}>

                                    {/* Card Header */}
                                    <h3 className="lv-booth-title">
                                        {txt.booth} : {booth}
                                    </h3>

                                    {/* Done / Remain Stats */}
                                    <div className="lv-stats-row">
                                        <div className="lv-stat-box done-box">
                                            <span>{txt.done}</span>
                                        </div>
                                        <div className="lv-stat-box remain-box">
                                            <span>{txt.remain}</span>
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <button
                                        className="lv-add-btn"
                                        onClick={(e) => handleAddVoting(booth, e)}
                                    >
                                        {txt.addVoting}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-muted p-5 bg-white rounded shadow-sm border slide-up-element">
                        <div style={{ fontSize: '40px', marginBottom: '16px' }}>📭</div>
                        <h4>{txt.noBooth}</h4>
                    </div>
                )}
            </div>

            {/* Modal Popup */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{txt.modalTitle} - {txt.booth} {selectedBooth}</h3>
                            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>×</button>
                        </div>

                        <div className="modal-body">
                            <div className="input-group">
                                <label>{txt.enterNumber}</label>
                                <div className="add-number-container">
                                    <input
                                        type="text"
                                        value={voterNumber}
                                        onChange={(e) => setVoterNumber(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Enter serial number..."
                                        className="number-input"
                                    />
                                    <button
                                        className="add-number-btn"
                                        onClick={handleAddNumber}
                                    >
                                        {txt.addNumber}
                                    </button>
                                </div>
                            </div>

                            <div className="numbers-list">
                                <label>{txt.addedNumbers}</label>
                                {voterNumbersList.length > 0 ? (
                                    <div className="numbers-grid">
                                        {voterNumbersList.map((num, idx) => (
                                            <div key={idx} className="number-tag">
                                                <span>{num}</span>
                                                <button
                                                    className="remove-number"
                                                    onClick={() => handleRemoveNumber(idx)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="no-numbers">{txt.noNumbers}</p>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setIsModalOpen(false)}>
                                {txt.cancel}
                            </button>
                            <button
                                className="submit-btn"
                                onClick={handleSubmit}
                                disabled={isSubmitting || voterNumbersList.length === 0}
                            >
                                {isSubmitting ? "Submitting..." : txt.submit}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                /* Page Layout */
                .lv-page-container {
                    width: 100%;
                    min-height: 100vh;
                    background: #eef2f6; 
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    padding-bottom: 40px;
                }
                .header-wrapper {
                    background: white;
                    padding: 0 24px;
                    position: sticky;
                    top: 0;
                    z-index: 50;
                }

                /* Card Styling matching the Image */
                .lv-card {
                    background: #ffffff;
                    border-radius: 12px;
                    border: 1px solid #475569; 
                    padding: 24px 20px;
                    text-align: center;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    cursor: pointer;
                }
                .lv-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 16px rgba(0,0,0,0.08);
                }

                /* Booth Title */
                .lv-booth-title {
                    color: #1e3a8a; 
                    font-weight: 800;
                    font-size: 20px;
                    margin: 0 0 20px 0;
                    letter-spacing: 0.5px;
                }

                /* Stats Row (Done & Remain) */
                .lv-stats-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 20px;
                }
                .lv-stat-box {
                    flex: 1;
                    padding: 10px;
                    border-radius: 8px;
                    background: #ffffff;
                    font-weight: 700;
                    font-size: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Specific Box Styles */
                .done-box {
                    border: 1.5px solid #16a34a; 
                    color: #16a34a;
                }
                .remain-box {
                    border: 1.5px solid #b91c1c; 
                    color: #b91c1c;
                }

                /* Add Voting Button */
                .lv-add-btn {
                    width: 100%;
                    background: #1e3a8a; 
                    color: #ffffff;
                    border: none;
                    border-radius: 8px;
                    padding: 14px;
                    font-size: 16px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: background 0.2s ease;
                }
                .lv-add-btn:hover {
                    background: #1e40af;
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                }
                .modal-content {
                    background: white;
                    border-radius: 16px;
                    width: 100%;
                    max-width: 500px;
                    max-height: 80vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 35px -10px rgba(0, 0, 0, 0.3);
                    animation: modalSlideIn 0.3s ease;
                }
                @keyframes modalSlideIn {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                .modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .modal-header h3 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 700;
                    color: #1e293b;
                }
                .modal-close-btn {
                    background: none;
                    border: none;
                    font-size: 28px;
                    cursor: pointer;
                    color: #94a3b8;
                    transition: color 0.2s;
                }
                .modal-close-btn:hover {
                    color: #1e293b;
                }
                .modal-body {
                    padding: 24px;
                }
                .input-group {
                    margin-bottom: 24px;
                }
                .input-group label {
                    display: block;
                    font-size: 14px;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 8px;
                }
                .add-number-container {
                    display: flex;
                    gap: 12px;
                }
                .number-input {
                    flex: 1;
                    padding: 12px 16px;
                    border: 1px solid #cbd5e1;
                    border-radius: 10px;
                    font-size: 14px;
                    outline: none;
                    transition: all 0.2s;
                }
                .number-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                .add-number-btn {
                    padding: 12px 20px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 10px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .add-number-btn:hover {
                    background: #2563eb;
                }
                .numbers-list label {
                    display: block;
                    font-size: 14px;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 12px;
                }
                .numbers-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                    max-height: 250px;
                    overflow-y: auto;
                }
                .number-tag {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #f1f5f9;
                    padding: 8px 12px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }
                .number-tag span {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1e293b;
                }
                .remove-number {
                    background: none;
                    border: none;
                    font-size: 18px;
                    cursor: pointer;
                    color: #ef4444;
                    padding: 0 4px;
                    transition: color 0.2s;
                }
                .remove-number:hover {
                    color: #dc2626;
                }
                .no-numbers {
                    text-align: center;
                    padding: 20px;
                    color: #94a3b8;
                    font-size: 14px;
                }
                .modal-footer {
                    padding: 16px 24px;
                    border-top: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                }
                .cancel-btn {
                    padding: 10px 20px;
                    background: #f1f5f9;
                    color: #475569;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .cancel-btn:hover {
                    background: #e2e8f0;
                }
                .submit-btn {
                    padding: 10px 24px;
                    background: #10b981;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .submit-btn:hover:not(:disabled) {
                    background: #059669;
                }
                .submit-btn:disabled {
                    background: #94a3b8;
                    cursor: not-allowed;
                }

                /* Animation */
                .slide-up-element {
                    animation: slideUp 0.4s ease forwards;
                    opacity: 0;
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default LiveVotingForCader;